import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { CustomerService } from "services/CustomerService";
import { GetSmsStatusHistory } from "services/Dtos/GetSmsMessagesDto";

interface SmsHistorySidebarProps {
  isOpen: boolean;
  onClose: () => void;
  smsId: number;
  message: string;
  apiKey: string;
}

const SmsHistorySidebar: React.FC<SmsHistorySidebarProps> = ({
  isOpen,
  onClose,
  smsId,
  message,
  apiKey,
}) => {
  const [history, setHistory] = useState<GetSmsStatusHistory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadHistory = async () => {
      if (!isOpen) return;

      try {
        setIsLoading(true);
        const response = await CustomerService.sms.getSmsHistoryBySmsId(
          smsId,
          apiKey,
        );
        setHistory(response.data || []);
      } catch (err) {
        setError("Failed to load SMS history");
        toast.error("Failed to load SMS history", {
          position: "top-right",
          autoClose: 3000,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadHistory();
  }, [isOpen, smsId]);

  const getStatusIcon = (eventType: string): string => {
    switch (eventType.toLowerCase()) {
      case "delivered":
        return "bi-check-circle";
      case "sent":
        return "bi-send";
      case "failed":
        return "bi-exclamation-triangle";
      case "bounce":
        return "bi-arrow-return-left";
      default:
        return "bi-info-circle";
    }
  };

  const getStatusColor = (eventType: string): string => {
    switch (eventType.toLowerCase()) {
      case "delivered":
      case "sent":
        return "text-success";
      case "failed":
      case "bounce":
        return "text-danger";
      default:
        return "text-info";
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`position-fixed top-0 end-0 bottom-0 start-0 bg-dark bg-opacity-50 ${
          !isOpen ? "d-none" : ""
        }`}
        onClick={onClose}
        style={{ zIndex: 1040 }}
      />

      {/* Sidebar */}
      <div
        className={`position-fixed top-0 end-0 bottom-0 bg-white shadow overflow-auto ${
          !isOpen ? "translate-end d-none" : ""
        }`}
        style={{ width: "400px", zIndex: 1045 }}
      >
        {/* Header */}
        <div className="p-3 border-bottom">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">SMS History</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-3">
          <div className="mb-4">
            <h6 className="mb-2">Message:</h6>
            <p className="mb-0">{message}</p>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="d-flex justify-content-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : history.length > 0 ? (
            <div className="timeline">
              {history.map((event, index) => (
                <div key={index} className="d-flex mb-3">
                  <div className="d-flex flex-column align-items-center me-3">
                    <div className="mb-2">
                      <i
                        className={`bi ${getStatusIcon(event.status)} ${getStatusColor(event.status)}`}
                      ></i>
                    </div>
                    {index !== history.length - 1 && (
                      <div
                        className="border-start border-secondary"
                        style={{ height: "100%", width: "2px" }}
                      ></div>
                    )}
                  </div>
                  <div className="card flex-grow-1">
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">{event.status}</h6>
                        <small className="text-muted">
                          {new Date(event.createdAt).toLocaleString()}
                        </small>
                      </div>
                      {event.errorMessage && (
                        <p className="mb-0 small text-danger mt-2">
                          {event.errorMessage}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="alert alert-info" role="alert">
              No history available for this SMS.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="position-sticky bottom-0 p-3 bg-white border-top">
          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>

      <style>
        {`
          .timeline {
            position: relative;
            padding: 20px 0;
          }
        `}
      </style>
    </>
  );
};

export default SmsHistorySidebar;
